<template>
    <!-- No id: the same card renders in both the map carousel and the list,
         which duplicated every id on the page, and nothing looked them up.
         The photo link repeats the title link below, so it is skipped by
         keyboard and screen readers rather than announced as a second,
         unnamed link to the same place. -->
    <article class="card-site" :class="type" v-if="item?.fields">
        <NuxtLink class="image-wrapper" :to="`${url}${item.id}`" @click.native="saveSite" aria-hidden="true" tabindex="-1">
            <mess-image :src="photo" placeholder loading="lazy" :sizes="imageSizes" />
            <!-- Over the photo, per the Figma (Asana O2-4). Always on the large
                 card; on the list card only from $tablet up, where the image is
                 273x165 and has room for it — see the CSS below. Rendered for
                 both, and the one that doesn't apply is display:none'd, which
                 also keeps it out of the accessibility tree rather than being
                 announced twice. -->
            <GlobalSiteLabel v-if="type === 'large' || type === 'list'" class="card-site-label" :site="item" />
        </NuxtLink>
        <div class="content">
            <div class="copy">
                <!-- In the copy instead of over the photo wherever the photo is
                     too small to carry it: the map carousel hides its image
                     outright (the label vanished with it), and below $tablet the
                     list card's image is a 70px thumbnail that "Saturday Only"
                     overhangs by 33px (Asana 1217504408291729). -->
                <GlobalSiteLabel v-if="type !== 'large'" class="card-site-label-inline" :site="item" />
                <!-- The large card shows its label only over the (hidden) photo
                     link, so screen readers get this copy instead. -->
                <GlobalSiteLabel v-else class="sr-only" :site="item" />
                <p v-if="type !== 'large' && neighborhoodName" class="gray-tag">{{ neighborhoodName }}</p>
                <!-- In map view the title is deliberately not a link: the card
                     carries an explicit Info button instead (Asana 1215600956750518). -->
                <h3 v-if="type === 'map'" class="headline-2">{{ item.fields['Site'] }}</h3>
                <NuxtLink v-else :to="`${url}${item.id}`" @click.native="saveSite">
                    <h3 class="headline-2">{{ item.fields['Site'] }}</h3>
                </NuxtLink>
                <p v-if="type === 'large' && neighborhoodName" class="gray-tag">{{ neighborhoodName }}</p>
                <p v-if="item && item.fields && item.fields['Site Address']" class="address" :class="{ 'body-small': type !== 'map', 'body-book': type === 'map' }">
                    {{ item.fields['Site Address'] }}
                    <span v-if="item.distance" class="distance">{{ distanceDisplay }} from you</span>
                </p>
            </div>
            <div class="footer">
                <GlobalUserActionButton list="ohcFavoriteSites" :item="item" :key="item.id" />
                <GlobalUserActionButton
                    v-if="isGhost"
                    :isGhost="isGhost"
                    list="ohcItineraries"
                    @click.prevent="updateSingleItem(item.id)"
                    :item="item"
                    :itinerary="itinerary"
                    :key="item.id + '-ghost'" />
                <GlobalUserActionButton v-else list="ohcItineraries" :item="item" :itinerary="itinerary" :key="item.id" />
                <NuxtLink
                    v-if="type === 'map'"
                    class="site-info-button"
                    :to="`${url}${item.id}`"
                    @click.native="saveSite"
                    :aria-label="`Site info for ${item.fields['Site']}`"
                >
                    <span>Info</span>
                    <SvgsRightArrow class="site-info-arrow" />
                </NuxtLink>
            </div>
        </div>
    </article>
</template>

<script setup>
    // Stores
    import { useNeighborhoodsStore } from '@/stores/neighborhoods';
    import { useUserStore } from '@/stores/user';
    const neighborhoodsStore = useNeighborhoodsStore();
    const neighborhoodsMap = neighborhoodsStore.neighborhoodsMap;
    const userStore = useUserStore();

    // Composables
    const { getSiteNeighborhoodName } = useUtils();
    const { getImageFromArray } = useImageUrl();

    // Emits
    const emit = defineEmits(['updateSingleItem']);

    // Props
    const props = defineProps({
        itinerary: {
            type: Object,
            required: false,
            default: {},
        },
        item: {
            type: Object,
            required: true,
        },
        type: {
            type: String,
            required: false,
            default: 'large',
        },
        isGhost: {
            type: Boolean,
            required: false,
            default: false,
        },
        // Lets a container that knows its own slot say so. `type` alone cannot
        // settle the width: the large card is 559px wide on an event page and
        // 326px in the homepage carousel, so its shared ladder has to cover the
        // larger of the two and over-serves the smaller. A container that knows
        // better passes its own.
        sizes: {
            type: String,
            required: false,
            default: '',
        },
    });
    const { isGhost } = toRefs(props);
    const { itinerary, item, type, sizes } = toRefs(props);

    // The three card types occupy very different slots (see SIZES_SITE_CARD
    // for the measurements), so the image has to be told which one it is in
    // rather than assuming a single width for all of them.
    const imageSizes = computed(() => sizes.value || SIZES_SITE_CARD[type.value] || SIZES_SITE_CARD.large);

    // Variables
    const neighborhoodName = getSiteNeighborhoodName(item, neighborhoodsMap);
    const route = useRoute();

    // Computed
    const url = computed(() => {
        if (route.path.includes('archive')) return '/archive/2023/sites/';
        else return '/sites/';
    });

    const photo = computed(() => {
        if (item.value?.fields?.['Selected Photos']) {
            return getImageFromArray(item.value.fields['Selected Photos']);
        }
        return '';
    });

    // Calculate distance if user location and site coordinates are available
    const distance = computed(() => {
        if (!userStore.location || !item.value?.fields?.Geocoordinates) {
            return null;
        }
        
        const coords = item.value.fields.Geocoordinates;
        const [siteLat, siteLng] = coords.split(',').map(coord => parseFloat(coord.trim()));
        
        if (isNaN(siteLat) || isNaN(siteLng)) {
            return null;
        }
        
        return userStore.calculateDistance(siteLat, siteLng);
    });

    // Format distance for display
    const distanceDisplay = computed(() => {
        if (item.value?.distance === null || item.value?.distance === undefined) return null;
        // If less than 0.1 miles, show in feet
        if (item.value.distance < 0.1) {
            const feet = Math.round(item.value.distance * 5280);
            return `${feet} ft`;
        }
        // Otherwise show in miles with 1 decimal place
        return `${item.value.distance.toFixed(1)} mi`;
    });

    // Methods
    const saveSite = () => {
        if (!item.value?.id) return;
        if (!window.siteQuery) {
            window.siteQuery = {
                savedSite: item.value.id,
            };
        } else {
            window.siteQuery.savedSite = item.value.id;
        }
    };
    function updateSingleItem(item) {
        emit('updateSingleItem', item);
    }
</script>

<style lang="scss" scoped>
    .card-site {
        background-color: $white;
        // Contains the large card's .sr-only label. Without it that
        // absolutely positioned label belongs to the carousel wrapper,
        // outside the carousel's scroll clip, and widened the homepage
        // to the full length of the Sites carousel (horizontal scroll).
        position: relative;

        .image-wrapper {
            display: block;
            position: relative;
            margin-bottom: 0.5em;
        }

        // Sits over the bottom-left of the photo, per Figma 4720-17696. The
        // wrapper is already positioned, and the image inside it is absolute, so
        // this needs a z-index to sit above it.
        .card-site-label {
            position: absolute;
            left: 0;
            bottom: 0;
            z-index: 1;
        }

        // Which of the two placements applies is a question of whether the image
        // is big enough to carry the label (Asana O2-4):
        //
        //   large        273px+ photo   -> always over the image
        //   list <tablet  70px thumb    -> in the copy (would overhang by 33px)
        //   list >=tablet 273x165 photo -> over the image, as the Figma shows
        //   map           image hidden  -> in the copy, or it disappears entirely
        //
        // Both are in the markup for the list card. The over-photo one sits in
        // the aria-hidden photo link, so at tablet+ the inline one is visually
        // hidden rather than display:none'd — it stays the copy screen readers
        // hear.
        &.list {
            .card-site-label {
                display: none;
            }

            @media (min-width: $tablet) {
                .card-site-label {
                    display: inline-flex;
                }

                .card-site-label-inline {
                    position: absolute;
                    width: 1px;
                    height: 1px;
                    padding: 0;
                    margin: -1px;
                    overflow: hidden;
                    clip-path: inset(50%);
                    white-space: nowrap;
                }
            }
        }

        // The list/map variant: in the flow above the neighborhood tag rather
        // than overlaid, so it is never wider than what contains it.
        .card-site-label-inline {
            margin-bottom: 8px;
        }

        img {
            position: absolute;
            inset: 0 0 0 0;
            height: 100%;
            width: 100%;
            object-fit: cover;
            object-position: center;
        }

        &.large {
            .image-wrapper {
                padding-top: 60%;
                height: 0;
                width: 100%;

                @media (min-width: $tablet) {
                    padding-top: 30%;
                    width: 50%;
                }
            }

            .gray-tag {
                margin: 10px 0;
            }

            .content {
                padding: 10px 0;
                width: 100%;
            }
        }

        &.list,
        &.map {
            display: flex;

            .image-wrapper {
                width: 70px;
                height: 70px;
                flex-shrink: 0;
                margin-right: 17px;
            }

            h3 {
                margin-bottom: 10px;
            }

            // Titles run to different lengths, so without this the action row
            // sat at a different height on every card in the carousel. Making
            // the content a full-height column lets the footer take up the slack
            // and line up across all of them.
            .content {
                display: flex;
                flex-direction: column;
                flex: 1;
                min-width: 0;
            }

            .footer {
                margin-top: auto;
            }
        }

        &.map {
            // Tightened from 15px/12px/12px/10px to give the map more of the
            // screen — the carousel is a fixed height subtracted from it, so
            // every pixel saved inside the card is a pixel the map gains
            // (Asana 1217333501985926 / 1217333292818329).
            padding: 12px;

            h3 {
                margin-bottom: 6px;
            }

            .gray-tag {
                margin: 0 0 8px;

                @media (min-width: $tablet) {
                    margin: 8px 0;
                }
            }

            .copy {
                margin-bottom: 8px;
            }

            .footer {
                margin-top: auto;
            }
        }

        &.list {
            padding: 20px 0;
            border-bottom: 1px solid #9d9fa2;

            .gray-tag {
                margin: 0 0 6px;
            }

            .content {
                display: flex;
                flex-grow: 1;
            }

            // A row at every width. This was a column below $tablet — a
            // leftover from when the buttons sat in their own narrow track down
            // the right-hand side of the card. Now they follow the copy, where
            // stacking them just made the card taller for no reason. The tablet
            // block below no longer needs to flip the direction, only the
            // spacing, so the two now match.
            .footer {
                flex-grow: 0;
                flex-shrink: 0;
                margin: 20px 0 0;
                flex-direction: row;

                button {
                    margin: 0;

                    + button {
                        margin-left: 12px;
                    }
                }
            }

            .copy {
                padding-right: 18px;
                flex-grow: 1;
            }
        }

        .footer {
            display: flex;
            margin-top: 10px;

            button {
                margin-right: 15px;
            }
        }

        // Map-view only: a pill sitting alongside the favourite and itinerary
        // circles, since the title is no longer the way into the site page.
        .site-info-button {
            display: inline-flex;
            align-items: center;
            gap: 8px;

            // Asymmetric on purpose. Flex centres the BOXES, but the caps sit
            // high inside their line box, so the label and arrow rendered 2px
            // above the pill's centre. The extra top padding pushes the ink onto
            // the centre line, which is what the eye reads.
            padding: 10px 14px 6px;
            border: 1px solid $gray-common;
            border-radius: 999px;

            font-family: $body;
            font-size: 12px;
            font-weight: $heavy;
            letter-spacing: 0.72px;
            line-height: 1;
            text-transform: uppercase;
            color: $black;

            &:hover,
            &:focus-visible {
                border-color: $black;
            }
        }

        .site-info-arrow {
            width: 16px;
            height: auto;

            // The arrow's ink fills its box, but the caps beside it sit high in
            // theirs — so centring the two boxes left the arrow 1.25px below the
            // letters. Nudged rather than margin'd so the pill's height is
            // unchanged.
            position: relative;
            top: -1.25px;
        }

        .headline-3 {
            padding-right: 9%;
        }

        @media (min-width: $tablet) {
            flex-direction: column;
            height: 100%;

            &.map {
                flex-direction: row;
            }

            &.list {
                border-bottom: none;
                padding: 0 0 5px 0;

                // The footer's direction and spacing are the same at every
                // width now, so this block no longer overrides them.
            }

            &.list .image-wrapper {
                margin-bottom: 15px;
                margin-right: 0px;
                width: 100%;
                height: 165px;
            }

            .content {
                flex-direction: column;
                display: flex;
            }
        }

        .distance {
            display: block;
            margin-top: 6px;
            font-size: 12px;
            color: #9d9fa2;
            font-weight: 500;
            white-space: nowrap;
        }
    }
</style>
